import { useCallback, useState } from "react";
import { TOUR_STEPS, TOUR_ROUTES } from "./tourSteps";

const ACTIVE_KEY = "sqAdminTourActive";
const STEP_KEY = "sqAdminTourStep";

function readActive() {
  return sessionStorage.getItem(ACTIVE_KEY) === "1";
}

function readStepIndex() {
  const n = parseInt(sessionStorage.getItem(STEP_KEY) || "0", 10);
  return Number.isFinite(n) && n >= 0 && n < TOUR_STEPS.length ? n : 0;
}

/**
 * Drives the admin walkthrough across the Rules -> Stage Quiz page pair.
 * Step/active state lives in sessionStorage so it survives the navigate()
 * between those two routes (each a full component remount).
 */
export function useAdminTour(page, navigate) {
  const [active, setActiveState] = useState(readActive);
  const [stepIndex, setStepIndexState] = useState(readStepIndex);

  const persist = useCallback((nextActive, nextStep) => {
    if (nextActive) {
      sessionStorage.setItem(ACTIVE_KEY, "1");
      sessionStorage.setItem(STEP_KEY, String(nextStep));
    } else {
      sessionStorage.removeItem(ACTIVE_KEY);
      sessionStorage.removeItem(STEP_KEY);
    }
    setActiveState(nextActive);
    setStepIndexState(nextStep);
  }, []);

  const start = useCallback(() => persist(true, 0), [persist]);
  const stop = useCallback(() => persist(false, 0), [persist]);

  const goNext = useCallback(() => {
    const next = stepIndex + 1;
    if (next >= TOUR_STEPS.length) {
      stop();
      return;
    }
    persist(true, next);
    const nextStep = TOUR_STEPS[next];
    if (nextStep.page !== page && navigate) navigate(TOUR_ROUTES[nextStep.page]);
  }, [stepIndex, page, navigate, persist, stop]);

  const goPrev = useCallback(() => {
    const prev = Math.max(0, stepIndex - 1);
    persist(true, prev);
    const prevStep = TOUR_STEPS[prev];
    if (prevStep.page !== page && navigate) navigate(TOUR_ROUTES[prevStep.page]);
  }, [stepIndex, page, navigate, persist]);

  const current = active ? TOUR_STEPS[stepIndex] : null;
  const step = current && current.page === page ? current : null;

  return {
    active,
    step,
    stepNumber: stepIndex + 1,
    totalSteps: TOUR_STEPS.length,
    isLast: stepIndex === TOUR_STEPS.length - 1,
    start,
    stop,
    goNext,
    goPrev,
  };
}
